import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { AuthGuard } from './AuthGuard';
import { type ReactNode } from 'react';

interface UserRoleGuardProps {
  children: ReactNode;
}

const SKIP_WINDOW_MS = 30000; // grace period after a merchant switch and redirect

function shouldSkipAutoSwitch(): boolean {
  const raw = localStorage.getItem('merchantSwitchTime');
  if (!raw) return false;
  const switchTime = parseInt(raw, 10);
  if (Number.isNaN(switchTime)) return false;
  const elapsed = Date.now() - switchTime;
  return elapsed >= 0 && elapsed < SKIP_WINDOW_MS;
}

/**
 * Guard that ensures the user is:
 * 1. Logged in (shows auth dialog if not)
 * 2. In 'user' role (auto-switches from 'merchant' if needed)
 *
 * Use for routes like /home and /family-plan that are
 * strictly for individual (non-merchant) users.
 */
export function UserRoleGuard({ children }: UserRoleGuardProps) {
  const { isAuthenticated, user, switchRole, isLoading } = useAuth();
  const { showToast } = useToast();
  const [isSwitching, setIsSwitching] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Only run the check once after auth state stabilises
    if (isLoading || hasChecked) return;

    // If the user just intentionally switched to merchant within the last few seconds,
    // don't switch them back immediately (prevents fighting with the manual switch button)
    if (shouldSkipAutoSwitch()) {
      setHasChecked(true);
      return;
    }

    // Only act when authenticated as merchant
    if (isAuthenticated && user?.activeRole === 'merchant' && !isSwitching) {
      setIsSwitching(true);
      switchRole('user')
        .then(() => {
          showToast('info', 'Switched to Individual account for this feature.');
        })
        .catch(() => {
          showToast('error', 'Failed to switch to Individual account. Please try again.');
          setIsSwitching(false);
        });
    }

    setHasChecked(true);
  }, [
    isLoading,
    hasChecked,
    isAuthenticated,
    user?.activeRole,
    isSwitching,
    switchRole,
    showToast,
  ]);

  // Show loading spinner while switching roles
  if (isSwitching) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3">
        <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-[#04BFBF] border-t-transparent"></div>
        <p className="text-sm text-slate-500">Switching to Individual account...</p>
      </div>
    );
  }

  return <AuthGuard requireAuth>{children}</AuthGuard>;
}
